const {
    shouldTranslateText,
    normalizeTranslatorLang
} = require('./rules')

const {
    DEFAULT_TRANSLATOR_OPTIONS,
    EXCLUDED_TAGS,
    EXCLUDED_CLASS_NAMES,
    TRANSLATOR_IGNORE_ATTR,
    shouldSkipText
} = require('./config')

class PageTranslatorEngine {
    constructor(options = {}) {
        this.options = {
            ...DEFAULT_TRANSLATOR_OPTIONS,
            ...options
        }
        this.translateBatch = options.translateBatch
        this.message = options.message || function () {}
        this.lang = 'none'
        this.originalTextMap = new WeakMap()
        this.originalPlaceholderMap = new WeakMap()
        this.translatedLangMap = new WeakMap()
        this.translatedPlaceholderLangMap = new WeakMap()
        this.translatedTextMap = new Map()
        this.knownNodes = new Set()
        this.knownPlaceholderElements = new Set()
        this.applyingNodes = new WeakSet()
        this.applyingPlaceholderElements = new WeakSet()
        this.memoryCache = new Map()
        this.failedCache = new Set()
        this.observer = null
        this.timer = null
        this.destroyed = false
        this.version = 0
        this.isApplying = false
    }

    setLanguage(lang) {
        const nextLang = normalizeTranslatorLang(lang)
        if (this.destroyed) return Promise.resolve()
        if (nextLang === this.lang && nextLang !== 'none') return this.translate()

        this.version++
        this.restore()
        if (nextLang !== this.lang) {
            this.failedCache.clear()
        }
        this.lang = nextLang

        if (this.lang === 'none') {
            this.stopObserve()
            return Promise.resolve()
        }

        this.startObserve()
        return this.translate()
    }

    translate() {
        if (this.destroyed || this.lang === 'none' || typeof document === 'undefined') {
            return Promise.resolve()
        }

        const currentVersion = this.version
        const items = this.collectTranslatableItems(this.lang)
        if (!items.length) return Promise.resolve()

        return this.translateItems(items, this.lang, currentVersion)
    }

    restore() {
        this.isApplying = true
        this.knownNodes.forEach(node => {
            this.restoreTextNode(node)
        })
        this.knownPlaceholderElements.forEach(element => {
            this.restorePlaceholderElement(element)
        })
        this.restoreCurrentTextNodes()
        this.restoreCurrentPlaceholders()
        this.isApplying = false
    }

    restoreTextNode(node) {
        if (!node) return false

        const original = this.originalTextMap.get(node)
        if (original !== undefined) {
            if (node.textContent !== original) {
                this.applyingNodes.add(node)
                node.textContent = original
                this.translatedLangMap.delete(node)
                return true
            }
            this.translatedLangMap.delete(node)
            return false
        }

        const mappedOriginal = this.translatedTextMap.get(node.textContent || '')
        if (mappedOriginal !== undefined && mappedOriginal !== null && node.textContent !== mappedOriginal) {
            this.applyingNodes.add(node)
            node.textContent = mappedOriginal
            this.originalTextMap.set(node, mappedOriginal)
            this.knownNodes.add(node)
            this.translatedLangMap.delete(node)
            return true
        }

        this.translatedLangMap.delete(node)
        return false
    }

    restorePlaceholderElement(element) {
        if (!element || typeof element.getAttribute !== 'function') return false

        const original = this.originalPlaceholderMap.get(element)
        if (original !== undefined) {
            if (element.getAttribute('placeholder') !== original) {
                this.applyingPlaceholderElements.add(element)
                element.setAttribute('placeholder', original)
                this.translatedPlaceholderLangMap.delete(element)
                return true
            }
            this.translatedPlaceholderLangMap.delete(element)
            return false
        }

        const currentPlaceholder = element.getAttribute('placeholder') || ''
        const mappedOriginal = this.translatedTextMap.get(currentPlaceholder)
        if (mappedOriginal !== undefined && mappedOriginal !== null && currentPlaceholder !== mappedOriginal) {
            this.applyingPlaceholderElements.add(element)
            element.setAttribute('placeholder', mappedOriginal)
            this.originalPlaceholderMap.set(element, mappedOriginal)
            this.knownPlaceholderElements.add(element)
            this.translatedPlaceholderLangMap.delete(element)
            return true
        }

        this.translatedPlaceholderLangMap.delete(element)
        return false
    }

    restoreCurrentTextNodes() {
        if (typeof document === 'undefined' || typeof NodeFilter === 'undefined') return
        const root = document.body
        if (!root) return

        const walker = document.createTreeWalker(
            root,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: node => {
                    if (!node || !node.parentElement) return NodeFilter.FILTER_REJECT
                    if (this.shouldRejectByParent(node.parentElement)) return NodeFilter.FILTER_REJECT
                    return this.translatedTextMap.has(node.textContent || '')
                        ? NodeFilter.FILTER_ACCEPT
                        : NodeFilter.FILTER_SKIP
                }
            }
        )

        while (walker.nextNode()) {
            this.restoreTextNode(walker.currentNode)
        }
    }

    restoreCurrentPlaceholders() {
        if (typeof document === 'undefined') return
        const root = document.body
        if (!root || typeof root.querySelectorAll !== 'function') return

        Array.prototype.forEach.call(root.querySelectorAll('input[placeholder], textarea[placeholder]'), element => {
            if (this.shouldRejectPlaceholderElement(element)) return
            const placeholder = element.getAttribute('placeholder') || ''
            if (this.translatedTextMap.has(placeholder)) this.restorePlaceholderElement(element)
        })
    }

    destroy() {
        this.destroyed = true
        this.version++
        this.stopObserve()
        if (this.timer) clearTimeout(this.timer)
        this.timer = null
        this.knownNodes.clear()
        this.knownPlaceholderElements.clear()
        this.memoryCache.clear()
        this.failedCache.clear()
        this.translatedTextMap.clear()
    }

    collectTranslatableItems(targetLang) {
        const textItems = this.collectTextNodes(targetLang, 0)
        return textItems.concat(this.collectPlaceholderItems(targetLang, textItems.length))
    }

    collectTextNodes(targetLang, startId = 0) {
        const result = []
        const root = document.body
        if (!root) return result

        const walker = document.createTreeWalker(
            root,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: node => this.acceptTextNode(node, targetLang)
            }
        )

        let id = 0
        while (walker.nextNode()) {
            const node = walker.currentNode
            const originalText = this.getOriginalText(node)
            if (this.translatedLangMap.get(node) === targetLang) continue
            result.push({
                id: String(startId + id),
                type: 'text',
                node,
                text: originalText.trim()
            })
            id++
        }
        return result
    }

    acceptTextNode(node, targetLang) {
        if (!node || !node.parentElement) return NodeFilter.FILTER_REJECT
        if (this.shouldRejectByParent(node.parentElement)) return NodeFilter.FILTER_REJECT

        const text = this.getOriginalText(node)
        if (shouldSkipText(text, node)) return NodeFilter.FILTER_SKIP
        return shouldTranslateText(text, targetLang) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
    }

    collectPlaceholderItems(targetLang, startId = 0) {
        const result = []
        const root = document.body
        if (!root || typeof root.querySelectorAll !== 'function') return result

        let id = 0
        Array.prototype.forEach.call(root.querySelectorAll('input[placeholder], textarea[placeholder]'), element => {
            if (this.shouldRejectPlaceholderElement(element)) return
            const originalText = this.getOriginalPlaceholder(element)
            if (this.translatedPlaceholderLangMap.get(element) === targetLang) return
            if (shouldSkipText(originalText, element)) return
            if (!shouldTranslateText(originalText, targetLang)) return
            result.push({
                id: String(startId + id),
                type: 'placeholder',
                element,
                text: originalText.trim()
            })
            id++
        })

        return result
    }

    shouldRejectByParent(element) {
        let current = element
        while (current && current !== document.body) {
            if (current.hasAttribute && current.hasAttribute(TRANSLATOR_IGNORE_ATTR)) return true
            if (EXCLUDED_TAGS.indexOf(current.tagName) > -1) return true
            if (this.hasExcludedClass(current)) return true
            current = current.parentElement
        }
        return false
    }

    hasExcludedClass(element) {
        if (!element || !element.classList) return false
        return EXCLUDED_CLASS_NAMES.some(className => element.classList.contains(className))
    }

    shouldRejectPlaceholderElement(element) {
        let current = element
        while (current && current !== document.body) {
            if (current.hasAttribute && current.hasAttribute(TRANSLATOR_IGNORE_ATTR)) return true
            if (current !== element && EXCLUDED_TAGS.indexOf(current.tagName) > -1) return true
            if (this.hasExcludedClass(current)) return true
            current = current.parentElement
        }
        return false
    }

    getOriginalText(node) {
        if (!this.originalTextMap.has(node)) {
            this.originalTextMap.set(node, node.textContent || '')
            this.knownNodes.add(node)
        }
        return this.originalTextMap.get(node)
    }

    getOriginalPlaceholder(element) {
        if (!this.originalPlaceholderMap.has(element)) {
            this.originalPlaceholderMap.set(element, element.getAttribute('placeholder') || '')
            this.knownPlaceholderElements.add(element)
        }
        return this.originalPlaceholderMap.get(element)
    }

    translateItems(items, targetLang, version) {
        if (typeof this.translateBatch !== 'function') return Promise.resolve()

        const pendingItems = []
        const cachedMap = {}

        items.forEach(item => {
            const cacheKey = this.getCacheKey(targetLang, item.text)
            if (this.memoryCache.has(cacheKey)) {
                cachedMap[item.id] = this.memoryCache.get(cacheKey)
            } else if (this.failedCache.has(cacheKey)) {
                return
            } else {
                pendingItems.push(item)
            }
        })

        this.fillItems(items, cachedMap, targetLang, version)

        if (!pendingItems.length) return Promise.resolve()

        const uniqueItems = this.createUniqueRequestItems(pendingItems, targetLang)
        const requestItems = this.createRequestItems(uniqueItems, this.options.maxItemChars)
        const requestTranslatedMap = {}
        const batches = this.createBatches(requestItems, this.options.batchSize, this.options.maxBatchChars)
        return this.runWithConcurrency(batches, batch => {
            const {
                sourceTexts,
                requestIdMap
            } = this.createBatchPayload(batch)
            return this.translateBatch(sourceTexts, targetLang).then(data => {
                Object.keys(requestIdMap).forEach(payloadId => {
                    const requestId = requestIdMap[payloadId]
                    if (data && data[payloadId] !== undefined && data[payloadId] !== null) {
                        requestTranslatedMap[requestId] = data[payloadId]
                    }
                })
            })
        }, this.options.concurrency).then(() => {
            const translatedMap = this.buildTranslatedMap(pendingItems, requestItems, requestTranslatedMap, targetLang)
            this.fillItems(pendingItems, translatedMap, targetLang, version)
        }).catch(() => {
            const translatedMap = this.buildTranslatedMap(pendingItems, requestItems, requestTranslatedMap, targetLang)
            this.fillItems(pendingItems, translatedMap, targetLang, version)
            const failedItems = this.markFailedItems(pendingItems, translatedMap, targetLang)
            if (failedItems.length) {
                this.message('翻译失败，请稍后重试')
            }
        })
    }

    applyCachedTranslations(targetLang, version) {
        if (this.destroyed || targetLang === 'none' || typeof document === 'undefined') return

        // 倒计时等组件会高频重写文本节点，先同步回填缓存翻译，避免原文短暂闪回。
        const items = this.collectTranslatableItems(targetLang)
        const cachedMap = {}
        let hasCachedItem = false

        items.forEach(item => {
            const cacheKey = this.getCacheKey(targetLang, item.text)
            if (!this.memoryCache.has(cacheKey)) return
            cachedMap[item.id] = this.memoryCache.get(cacheKey)
            hasCachedItem = true
        })

        if (hasCachedItem) this.fillItems(items, cachedMap, targetLang, version)
    }

    markFailedItems(items, translatedMap, targetLang) {
        return items.filter(item => {
            if (translatedMap[item.id] !== undefined && translatedMap[item.id] !== null) return false
            this.failedCache.add(this.getCacheKey(targetLang, item.text))
            return true
        })
    }

    createUniqueRequestItems(items, targetLang) {
        const cacheKeys = new Set()
        return items.filter(item => {
            const cacheKey = this.getCacheKey(targetLang, item.text)
            if (cacheKeys.has(cacheKey)) return false
            cacheKeys.add(cacheKey)
            return true
        })
    }

    createBatchPayload(batch) {
        return batch.reduce((result, item, index) => {
            const payloadId = String(index)
            result.sourceTexts[payloadId] = item.text
            result.requestIdMap[payloadId] = item.requestId
            return result
        }, {
            sourceTexts: {},
            requestIdMap: {}
        })
    }

    createRequestItems(items, maxItemChars) {
        const requestItems = []
        const maxChars = Math.max(1, maxItemChars || DEFAULT_TRANSLATOR_OPTIONS.maxItemChars)

        items.forEach(item => {
            const text = String(item.text || '')
            if (text.length <= maxChars) {
                requestItems.push({
                    ...item,
                    requestId: item.id,
                    sourceId: item.id,
                    partIndex: 0,
                    totalParts: 1
                })
                return
            }

            const totalParts = Math.ceil(text.length / maxChars)
            for (let index = 0; index < totalParts; index++) {
                requestItems.push({
                    ...item,
                    requestId: `${item.id}__part_${index}`,
                    sourceId: item.id,
                    partIndex: index,
                    totalParts,
                    text: text.slice(index * maxChars, (index + 1) * maxChars)
                })
            }
        })

        return requestItems
    }

    createBatches(items, batchSize, maxBatchChars) {
        const batches = []
        const maxItems = Math.max(1, batchSize || DEFAULT_TRANSLATOR_OPTIONS.batchSize)
        const maxChars = Math.max(1, maxBatchChars || DEFAULT_TRANSLATOR_OPTIONS.maxBatchChars)
        let currentBatch = []
        let currentChars = 0

        items.forEach(item => {
            const itemChars = String(item.text || '').length
            const shouldFlush = currentBatch.length > 0 && (
                currentBatch.length >= maxItems ||
                currentChars + itemChars > maxChars
            )

            if (shouldFlush) {
                batches.push(currentBatch)
                currentBatch = []
                currentChars = 0
            }

            currentBatch.push(item)
            currentChars += itemChars
        })

        if (currentBatch.length) {
            batches.push(currentBatch)
        }
        return batches
    }

    buildTranslatedMap(sourceItems, requestItems, requestTranslatedMap, targetLang) {
        const grouped = requestItems.reduce((map, item) => {
            if (!map[item.sourceId]) map[item.sourceId] = []
            map[item.sourceId].push(item)
            return map
        }, {})

        const sourceTextMap = sourceItems.reduce((map, item) => {
            const cacheKey = this.getCacheKey(targetLang, item.text)
            if (!map[cacheKey]) map[cacheKey] = []
            map[cacheKey].push(item)
            return map
        }, {})

        Object.keys(grouped).forEach(sourceId => {
            const sourceItem = sourceItems.find(item => item.id === sourceId)
            if (!sourceItem) return
            const parts = grouped[sourceId] || []
            const translatedParts = parts
                .sort((a, b) => a.partIndex - b.partIndex)
                .map(part => requestTranslatedMap[part.requestId])

            if (!translatedParts.length || !translatedParts.every(part => part !== undefined && part !== null)) return

            const translatedText = translatedParts.join('')
            const cacheKey = this.getCacheKey(targetLang, sourceItem.text)
            this.memoryCache.set(cacheKey, translatedText)
        })

        return Object.keys(sourceTextMap).reduce((translatedMap, cacheKey) => {
            if (!this.memoryCache.has(cacheKey)) return translatedMap
            const translatedText = this.memoryCache.get(cacheKey)
            sourceTextMap[cacheKey].forEach(item => {
                translatedMap[item.id] = translatedText
            })
            return translatedMap
        }, {})
    }

    runWithConcurrency(items, worker, concurrency) {
        const queue = items.slice()
        const workers = []
        const size = Math.max(1, concurrency || 1)
        let hasError = false

        for (let i = 0; i < size; i++) {
            workers.push(new Promise(resolve => {
                const next = () => {
                    if (!queue.length) {
                        resolve()
                        return
                    }
                    const item = queue.shift()
                    worker(item).then(next).catch(() => {
                        hasError = true
                        next()
                    })
                }
                next()
            }))
        }

        return Promise.all(workers).then(() => {
            if (hasError) throw new Error('translate batch failed')
        })
    }

    fillItems(items, translatedMap, targetLang, version) {
        if (this.destroyed || version !== this.version || !translatedMap) return

        this.isApplying = true
        items.forEach(item => {
            const translated = translatedMap[item.id]
            if (translated === undefined || translated === null) return
            if (item.type === 'placeholder') {
                this.fillPlaceholderItem(item, translated, targetLang)
                return
            }
            this.fillTextItem(item, translated, targetLang)
        })
        this.isApplying = false
    }

    fillTextItem(item, translated, targetLang) {
        if (!item.node || !item.node.parentElement) return
        const original = this.originalTextMap.get(item.node) || item.text
        this.rememberTranslatedText(translated, original)
        this.applyingNodes.add(item.node)
        item.node.textContent = translated
        this.translatedLangMap.set(item.node, targetLang)
    }

    fillPlaceholderItem(item, translated, targetLang) {
        if (!item.element || typeof item.element.setAttribute !== 'function') return
        const original = this.originalPlaceholderMap.get(item.element) || item.text
        this.rememberTranslatedText(translated, original)
        this.applyingPlaceholderElements.add(item.element)
        item.element.setAttribute('placeholder', translated)
        this.translatedPlaceholderLangMap.set(item.element, targetLang)
    }

    rememberTranslatedText(translated, original) {
        const translatedText = String(translated || '')
        const originalText = String(original || '')
        if (!translatedText || !originalText || translatedText === originalText) return

        if (!this.translatedTextMap.has(translatedText)) {
            this.translatedTextMap.set(translatedText, originalText)
            return
        }

        if (this.translatedTextMap.get(translatedText) !== originalText) {
            this.translatedTextMap.set(translatedText, null)
        }
    }

    startObserve() {
        if (this.observer || typeof MutationObserver === 'undefined' || typeof document === 'undefined') return
        this.observer = new MutationObserver(mutations => {
            if (this.isApplying || this.lang === 'none') return
            let shouldSchedule = false
            mutations.forEach(mutation => {
                if (mutation.type === 'characterData' && mutation.target) {
                    if (this.applyingNodes.has(mutation.target)) {
                        this.applyingNodes.delete(mutation.target)
                        return
                    }
                    this.originalTextMap.set(mutation.target, mutation.target.textContent || '')
                    this.translatedLangMap.delete(mutation.target)
                    this.knownNodes.add(mutation.target)
                    shouldSchedule = true
                    return
                }
                if (mutation.type === 'attributes' && mutation.attributeName === 'placeholder' && mutation.target) {
                    if (this.handlePlaceholderMutation(mutation.target)) shouldSchedule = true
                    return
                }
                shouldSchedule = true
            })
            if (shouldSchedule) {
                this.applyCachedTranslations(this.lang, this.version)
                this.scheduleTranslate()
            }
        })
        this.observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['placeholder']
        })
    }

    handlePlaceholderMutation(element) {
        if (!element) return false

        if (this.applyingPlaceholderElements.has(element)) {
            this.applyingPlaceholderElements.delete(element)
            return false
        }

        this.originalPlaceholderMap.set(element, element.getAttribute('placeholder') || '')
        this.translatedPlaceholderLangMap.delete(element)
        this.knownPlaceholderElements.add(element)
        return true
    }

    stopObserve() {
        if (!this.observer) return
        this.observer.disconnect()
        this.observer = null
    }

    scheduleTranslate() {
        if (this.timer) clearTimeout(this.timer)
        this.timer = setTimeout(() => {
            this.timer = null
            this.translate()
        }, this.options.debounceTime)
    }

    getCacheKey(targetLang, text) {
        return `${targetLang}:${text}`
    }
}

module.exports = {
    PageTranslatorEngine
}
